import {assertAndContinue} from 'Asserts';
import {SchemaInterfaceExtendingSchema, TagSchema} from 'constants/RegistryConstants';
import {readFragmentFromCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLArrayToREST, graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {AssaySchema, ResultSchema} from 'results/assay-schemas/AssaySchemaConstants';
import {
  BoxSchema,
  ContainerSchema,
  LocationSchema,
  PlateSchema,
  StorableSchema,
} from 'samples/storables/StorableSchemaConstants';
import {getTranslatedTagSchemaFromCache} from 'schemas/util/getTagSchemaFromCache';
import APIUtil from 'util/APIUtil';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {SchemaInterfaceWithFieldsGraphQL} from '../../graphql-fragments/schemas/schema-interfaces/__generated/SchemaInterfaceWithFieldsGraphQL';
import {mapSchemaInterfaceGraphQLToSchemaInterfaceExtendingSchema} from './LookupConfigUtils';

export function getAllBoxSchemasFromCache(): Array<BoxSchema> {
  const cacheResult = readFragmentFromCache(getFragmentWrapper('AllBoxSchemas'));
  return cacheResult ? graphQLArrayToREST('BoxSchema', cacheResult.boxSchemas) : [];
}

export function getAllContainerSchemasFromCache(): Array<ContainerSchema> {
  const cacheResult = readFragmentFromCache(getFragmentWrapper('AllContainerSchemas'));
  return cacheResult ? graphQLArrayToREST('ContainerSchema', cacheResult.containerSchemas) : [];
}

export function getAllLocationSchemasFromCache(): Array<LocationSchema> {
  const cacheResult = readFragmentFromCache(getFragmentWrapper('AllLocationSchemas'));
  return cacheResult ? graphQLArrayToREST('LocationSchema', cacheResult.locationSchemas) : [];
}

export function getAllPlateSchemasFromCache(): Array<PlateSchema> {
  const cacheResult = readFragmentFromCache(getFragmentWrapper('AllPlateSchemas'));
  return cacheResult ? graphQLArrayToREST('PlateSchema', cacheResult.plateSchemas) : [];
}

export function getAllResultSchemasFromCache(): Array<ResultSchema> {
  const cacheResult = readFragmentFromCache(getFragmentWrapper('AllResultSchemas'));
  return cacheResult ? graphQLArrayToREST('ResultSchema', cacheResult.resultSchemas) : [];
}

export function getAllTagSchemasFromCache(): Array<TagSchema> {
  const cacheResult = readFragmentFromCache(getFragmentWrapper('AllCompleteTagSchemas'));
  return cacheResult ? graphQLArrayToREST('TagSchema', cacheResult.tagSchemas) : [];
}

export function getAllProcessTaskSchemasFromCache(): Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL> {
  const cacheResult = readFragmentFromCache(getFragmentWrapper('AllProcessTaskSchemasForSchemaConfig'));
  return cacheResult ? cacheResult.processTaskSchemasSupportingPlatformFeature : [];
}

export function getAllSchemaInterfacesFromCache(): Array<SchemaInterfaceWithFieldsGraphQL> {
  const cacheResult = readFragmentFromCache(getFragmentWrapper('AllSchemaInterfaces'));
  return cacheResult ? cacheResult.schemaInterfaces : [];
}

export function getAllSchemaInterfacesExtendingSchemaFromCache(): Array<SchemaInterfaceExtendingSchema> {
  const cacheResult = readFragmentFromCache(getFragmentWrapper('AllSchemaInterfaces'));
  return cacheResult
    ? cacheResult.schemaInterfaces.map((schemaInterface) =>
        mapSchemaInterfaceGraphQLToSchemaInterfaceExtendingSchema(schemaInterface)
      )
    : [];
}

export function getAssaySchemaFromCache(schemaId: string): AssaySchema | null {
  const cacheResult = readFragmentFromCache(getFragmentWrapper('AssaySchema'), schemaId);
  return cacheResult ? graphQLToREST('AssaySchema', cacheResult) : null;
}

export function getBoxSchemaFromCache(schemaId: string): BoxSchema | null {
  const cacheResult = readFragmentFromCache(getFragmentWrapper('BoxSchema'), schemaId);
  return cacheResult ? graphQLToREST('BoxSchema', cacheResult) : null;
}

export function getContainerSchemaFromCache(schemaId: string): ContainerSchema | null {
  const cacheResult = readFragmentFromCache(getFragmentWrapper('ContainerSchema'), schemaId);
  return cacheResult ? graphQLToREST('ContainerSchema', cacheResult) : null;
}

export function getLocationSchemaFromCache(schemaId: string): LocationSchema | null {
  const cacheResult = readFragmentFromCache(getFragmentWrapper('LocationSchema'), schemaId);
  return cacheResult ? graphQLToREST('LocationSchema', cacheResult) : null;
}

export function getPlateSchemaFromCache(schemaId: string): PlateSchema | null {
  const cacheResult = readFragmentFromCache(getFragmentWrapper('PlateSchema'), schemaId);
  return cacheResult ? graphQLToREST('PlateSchema', cacheResult) : null;
}

export function getStorableSchemaFromCache(schemaId: string): StorableSchema | null {
  if (APIUtil.isBoxSchema(schemaId)) {
    return getBoxSchemaFromCache(schemaId);
  } else if (APIUtil.isContainerSchema(schemaId)) {
    return getContainerSchemaFromCache(schemaId);
  } else if (APIUtil.isLocationSchema(schemaId)) {
    return getLocationSchemaFromCache(schemaId);
  } else {
    assertAndContinue(APIUtil.isPlateSchema(schemaId), `Expected storable schema, got ${schemaId}`);
    return getPlateSchemaFromCache(schemaId);
  }
}

export function getSchemaInterfaceFromCache(
  schemaInterfaceId: string
): SchemaInterfaceWithFieldsGraphQL | null {
  return readFragmentFromCache(getFragmentWrapper('SchemaInterfaceWithFields'), schemaInterfaceId);
}

export function getSchemaInterfaceExtendingSchemaFromCache(
  schemaId: string
): SchemaInterfaceExtendingSchema | null {
  const cacheResult = readFragmentFromCache(getFragmentWrapper('SchemaInterfaceWithFields'), schemaId);
  return cacheResult ? mapSchemaInterfaceGraphQLToSchemaInterfaceExtendingSchema(cacheResult) : null;
}

export function getTagSchemaOrSchemaInterfaceFromCache(
  schemaId: string
): TagSchema | SchemaInterfaceExtendingSchema | null {
  if (APIUtil.isTagSchema(schemaId)) {
    return getTranslatedTagSchemaFromCache(schemaId);
  } else {
    assertAndContinue(
      APIUtil.isSchemaInterface(schemaId),
      `Expected schema interface or tag schema, got ${schemaId}`
    );
    return getSchemaInterfaceExtendingSchemaFromCache(schemaId);
  }
}

export function getProcessTaskSchemaFromCache(
  schemaId: string
): ProcessTaskSchemaWithoutFlowchartConfigGraphQL | null {
  return readFragmentFromCache(getFragmentWrapper('ProcessTaskSchemaWithoutFlowchartConfig'), schemaId);
}
